import React, { useState } from "react";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import {
  updatePartInternalId,
} from "../services/PartsInventory";
import {
  updateVehicleInternalId,
} from "../services/VehicleService";

/**
 * Botão pequeno (ícone lápis) que abre modal para editar o ID interno
 * (parts_internal_id ou vehicle_internal_id). A validação de unicidade
 * é feita no backend — quando duplica, o servidor devolve 409 e
 * mostramos mensagem amigável.
 *
 * Props:
 * - kind: "part" | "vehicle"
 * - uuid: string (uuid do registo)
 * - currentValue: string (valor actual a pré-preencher)
 * - onUpdated: (newValue) => void  callback após sucesso
 */
const EditInternalIdButton = ({ kind, uuid, currentValue, onUpdated }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState(currentValue || "");
  const [saving, setSaving] = useState(false);

  const handleOpen = (e) => {
    e?.stopPropagation?.();
    e?.preventDefault?.();
    setValue(currentValue || "");
    setOpen(true);
  };
  const handleClose = () => {
    if (!saving) setOpen(false);
  };

  const handleSave = async () => {
    const v = (value || "").trim();
    if (!v || v === (currentValue || "").trim()) {
      setOpen(false);
      return;
    }
    setSaving(true);
    try {
      if (kind === "part") {
        await updatePartInternalId({ uuid, parts_internal_id: v });
      } else {
        await updateVehicleInternalId({ uuid, vehicle_internal_id: v });
      }
      toast.success(t("internal_id_updated") || "ID interno atualizado");
      onUpdated && onUpdated(v);
      setOpen(false);
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.message || "";
      if (status === 409 || msg.includes("duplicate")) {
        toast.error(
          t("duplicate_internal_id") ||
            "Já existe outro registo com este ID. Escolha outro.",
        );
      } else if (msg === "invalid_internal_id_format") {
        toast.error(
          t("invalid_internal_id_format") ||
            "ID inválido — usar A-Z, 0-9, hífen (máx 20)",
        );
      } else {
        toast.error(t(msg) || msg || t("error_general") || "Erro ao atualizar ID interno");
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="btn btn-sm btn-link p-0 ms-2"
        onClick={handleOpen}
        title={t("edit_internal_id") || "Editar ID interno"}
        style={{ verticalAlign: "middle" }}
      >
        <i className="bi bi-pencil-square" />
      </button>
      <Modal isOpen={open} toggle={handleClose} centered>
        <ModalHeader toggle={handleClose}>
          {t("edit_internal_id") || "Editar ID interno"}
        </ModalHeader>
        <ModalBody>
          <input
            type="text"
            className="form-control"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder={currentValue || ""}
            autoFocus
          />
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={saving}
          >
            {t("cancel") || "Cancelar"}
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving
              ? t("saving") || "A guardar..."
              : t("save") || "Guardar"}
          </button>
        </ModalFooter>
      </Modal>
    </>
  );
};

export default EditInternalIdButton;
